import type {
  MatrixData,
  MatrixDiffData,
  MatrixImportPreview,
  MatrixSaved,
  MatrixSubjects,
  MatrixVersionData,
  MatrixVersionItem,
  OfficeUserItem,
  OfficeUserSaved,
  RoleItem,
  RoleList,
  SetupSource,
  SetupStatus,
  TemporaryPasswordResult,
  UserList,
} from '@felix/ui';
import { ApiError, api, officeApi } from './api.js';

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Matris çalışma kitabının yüklenmesi: ham `.xlsx` gövdesi (katalog içe aktarma kalıbı). */
async function uploadMatrix(file: Blob): Promise<MatrixImportPreview> {
  let response: Response;
  try {
    response = await fetch('/api/office/matrices/import', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': XLSX_TYPE },
      body: file,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  if (!response.ok) {
    throw new ApiError(
      data.error ?? 'INTERNAL',
      response.status,
      typeof data.detail === 'string' ? data.detail : undefined,
      data,
    );
  }
  return data as MatrixImportPreview;
}

/**
 * Ofiste şirket kurulumu (ADR 0042 §22–§24): sihirbaz durumu, roller, kullanıcılar ve matris
 * editörü. Yalnız web ofis; gemide matrisler salt okunurdur.
 */
export const officeSetupSource: SetupSource = {
  setupStatus: () => api<SetupStatus>('/office/setup-status'),
  vessels: async () => (await officeApi.vessels()).map((v) => ({ id: v.id, name: v.name })),
  roles: () => api<RoleList>('/office/roles'),
  createRole: (input) => api<RoleItem>('/office/roles', { method: 'POST', body: input }),
  updateRole: (id, rowVersion, input) =>
    api<RoleItem>(`/office/roles/${id}`, { method: 'PATCH', body: { rowVersion, ...input } }),
  retireRole: (id, rowVersion) =>
    api<RoleItem>(`/office/roles/${id}/retire`, { method: 'POST', body: { rowVersion } }),
  users: () => api<UserList>('/office/users'),
  createUser: (input) =>
    api<TemporaryPasswordResult>('/office/users', { method: 'POST', body: input }),
  updateUser: (id, rowVersion, fullName) =>
    api<OfficeUserItem>(`/office/users/${id}`, { method: 'PATCH', body: { rowVersion, fullName } }),
  // Giriş e-postasını düzeltme (ADR 0145)
  changeEmail: (id, rowVersion, email, password) =>
    api<OfficeUserItem>(`/office/users/${id}/email`, {
      method: 'POST',
      body: { rowVersion, email, password },
    }),
  // Yönetici hesabına geçişte geçici parolası düşen kişi yanıtta (ADR 0150 §6)
  setCompanyRoles: (id, roles) =>
    api<OfficeUserSaved>(`/office/users/${id}/company-roles`, { method: 'PUT', body: { roles } }),
  // Yüklenen satırların sürümleri (ADR 0155 K7): arada değişen küme 409 ROW_VERSION_CONFLICT
  setVesselAssignments: (id, assignments, expected) =>
    api<OfficeUserSaved>(`/office/users/${id}/vessel-assignments`, {
      method: 'PUT',
      body: { assignments, ...(expected ? { expected } : {}) },
    }),
  disableUser: (id, rowVersion, password) =>
    api<OfficeUserItem>(`/office/users/${id}/disable`, {
      method: 'POST',
      body: { rowVersion, password },
    }),
  enableUser: (id, rowVersion, password) =>
    api<OfficeUserItem>(`/office/users/${id}/enable`, {
      method: 'POST',
      body: { rowVersion, password },
    }),
  resetPassword: (id, rowVersion, password) =>
    api<TemporaryPasswordResult>(`/office/users/${id}/reset-password`, {
      method: 'POST',
      body: { rowVersion, password },
    }),
  resetTotp: (id, password) =>
    api<OfficeUserItem>(`/office/users/${id}/reset-totp`, { method: 'POST', body: { password } }),
  matrix: () => api<MatrixData>('/office/matrices/current'),
  matrixVersions: () => api<MatrixVersionItem[]>('/office/matrices/versions'),
  matrixVersion: (version) => api<MatrixVersionData>(`/office/matrices/versions/${version}`),
  matrixDiff: (from, to) => api<MatrixDiffData>(`/office/matrices/diff?from=${from}&to=${to}`),
  matrixSubjects: () => api<MatrixSubjects>('/office/matrices/subjects'),
  saveMatrix: (input) => api<MatrixSaved>('/office/matrices', { method: 'POST', body: input }),
  matrixExportUrl: (version) => `/api/office/matrices/export.xlsx?version=${version}`,
  importMatrix: uploadMatrix,
};
